
import React, { useState, useEffect, useMemo } from 'react';
import { LucideIcon, Search, Filter } from 'lucide-react';
import PageHeader from './PageHeader';
import PageSection from './PageSection';
import { Topic } from '../../types/topic';
import { topicService } from '../../services/topicService';
import { useSectionProgress } from '../../hooks/useSectionProgress';

interface SectionMetadata {
  title: string;
  icon?: LucideIcon;
  description?: string;
  showDivider?: boolean;
  cardVariant?: 'default' | 'compact' | 'detailed';
  gridCols?: 1 | 2 | 3 | 4;
  maxRows?: number; // Maximum number of rows to show initially (default: 2)
  showProgress?: boolean;
  showStats?: boolean;
  hideHeader?: boolean;
}

interface SectionsPageProps {
  pageKey: string; // e.g., 'system-design', 'languages'
  pageTitle: string;
  pageDescription: string;
  pageIcon: LucideIcon;
  iconColor?: string;
  gradientColors?: {
    from: string;
    via?: string;
    to: string;
  };
  sections: Record<string, SectionMetadata>; // Section key -> metadata mapping
  defaultSection?: string; // Default section for topics without a section
  enableSearch?: boolean;
  enableFilter?: boolean;
  filterOptions?: Array<{ value: string; label: string }>;
  className?: string;
}

/**
 * Generic reusable page component for displaying pages with multiple sections
 * Handles fetching topics, grouping by sections, search, and filtering
 */
const SectionsPage: React.FC<SectionsPageProps> = ({
  pageKey,
  pageTitle,
  pageDescription,
  pageIcon: PageIcon,
  iconColor = 'text-primary-600 dark:text-primary-400',
  gradientColors,
  sections,
  defaultSection = 'other',
  enableSearch = false,
  enableFilter = false,
  filterOptions = [],
  className = '',
}) => {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  // Fetch section progress for this page (e.g. 'system-design', 'languages')
  const { progress } = useSectionProgress(pageKey);

  // Build a progress map: topic slug → percentage (simple: if slug is in completedLessons, 100%, else 0%)
  const progressMap = useMemo(() => {
    const map: Record<string, number> = {};
    if (progress.completedLessons.length > 0) {
      topics.forEach((topic) => {
        const isCompleted = progress.completedLessons.includes(topic.slug) ||
          progress.completedLessons.includes(topic.id);
        map[topic.slug] = isCompleted ? 100 : 0;
        map[topic.id] = isCompleted ? 100 : 0;
      });
    }
    return map;
  }, [topics, progress.completedLessons]);

  // Fetch topics on mount
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await topicService.getAll({ page: pageKey, limit: 100 });
        setTopics(response.topics);
      } catch (err: any) {
        setError(err.message || 'Failed to load topics');
      } finally {
        setLoading(false);
      }
    };

    fetchTopics();
  }, [pageKey]);

  // Filter and group topics by section
  const groupedTopics = useMemo(() => {
    // Apply search filter
    let filtered = topics;
    if (searchTerm) {
      filtered = topics.filter(
        (topic) =>
          topic.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          topic.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply difficulty filter
    if (selectedFilter !== 'all') {
      filtered = filtered.filter((topic) => topic.difficulty === selectedFilter);
    }

    // Group by section
    const grouped: Record<string, Topic[]> = {};
    filtered.forEach((topic) => {
      const section = topic.section || defaultSection;
      if (!grouped[section]) {
        grouped[section] = [];
      }
      grouped[section].push(topic);
    });

    return grouped;
  }, [topics, searchTerm, selectedFilter, defaultSection]);

  // Get section order (use order from sections config or alphabetical)
  const sectionOrder = useMemo(() => {
    return Object.keys(sections).sort((a, b) => {
      // If both sections exist in config, maintain their order
      const aIndex = Object.keys(sections).indexOf(a);
      const bIndex = Object.keys(sections).indexOf(b);
      return aIndex - bIndex;
    });
  }, [sections]);

  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 pt-16 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <PageHeader
          title={pageTitle}
          description={pageDescription}
          icon={PageIcon}
          iconColor={iconColor}
          gradientColors={gradientColors}
        />

        {/* Search and Filters */}
        {(enableSearch || enableFilter) && (
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              {enableSearch && (
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder={`Search ${pageTitle.toLowerCase()}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input-field pl-10"
                  />
                </div>
              )}
              {enableFilter && filterOptions.length > 0 && (
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedFilter}
                    onChange={(e) => setSelectedFilter(e.target.value)}
                    className="input-field pl-10 pr-8 appearance-none"
                  >
                    {filterOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        )}

        {loading && (
          <div className="sections-loading" role="status" aria-label={`Loading ${pageTitle} topics`}>
            <span className="sr-only">Loading topics...</span>
            <div className="sections-loading__heading" aria-hidden="true" />
            <div className="sections-loading__grid" aria-hidden="true">
              {Array.from({ length: 3 }).map((_, index) => (
                <div className="sections-loading__card" key={index}>
                  <div className="sections-loading__line sections-loading__line--title" />
                  <div className="sections-loading__line" />
                  <div className="sections-loading__line sections-loading__line--short" />
                  <div className="sections-loading__meta" />
                </div>
              ))}
            </div>
          </div>
        )}

        {error && !loading && (
          <div className="sections-inline-error" role="alert">
            <PageIcon aria-hidden="true" />
            <div>
              <h3>Topics could not be loaded</h3>
              <p>{error}</p>
            </div>
          </div>
        )}

        {/* Render Sections once their data is ready. */}
        {!loading && !error && sectionOrder.map((sectionKey) => {
          const sectionTopics = groupedTopics[sectionKey] || [];
          const sectionMetadata = sections[sectionKey];

          // Skip section if no topics and section not explicitly defined in config
          if (sectionTopics.length === 0 && !sectionMetadata) {
            return null;
          }

          // Use default metadata if section not in config
          const metadata: SectionMetadata = sectionMetadata || {
            title: sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1).replace(/-/g, ' '),
            cardVariant: 'default',
            gridCols: 3,
            maxRows: 2,
          };

          return (
            <PageSection
              key={sectionKey}
              sectionKey={sectionKey}
              sectionMetadata={metadata}
              topics={sectionTopics}
              cardVariant={metadata.cardVariant || 'default'}
              gridCols={metadata.gridCols || 3}
              showProgress={metadata.showProgress || false}
              showStats={metadata.showStats || false}
              hideHeader={metadata.hideHeader || false}
              progressMap={progressMap}
            />
          );
        })}

        {/* No Results */}
        {!loading && !error && Object.keys(groupedTopics).length === 0 && (
          <div className="text-center py-12">
            <PageIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No topics found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Try adjusting your search or filter criteria
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SectionsPage;
